// Vernier Caliper / Micrometer Inspection — multi-dimension manual entry.
// Each row is a measured feature with nominal + tol + actual + result.
import React, { useEffect, useState } from 'react';
import {
  Box, Card, Typography, Button, TextField, MenuItem, Stack, Chip, IconButton,
} from '@mui/material';
import StraightenIcon from '@mui/icons-material/Straighten';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import AddIcon from '@mui/icons-material/Add';
import { customerbasedshift, shiftadd } from '../../Services/app/masterservice';
import { cleanCustomerId } from '../../Services/app/operatorservice';

const STORAGE_KEY = 'quality_inspections_vernier';
const INSTRUMENTS = ['Vernier Caliper 0–150mm', 'Vernier Caliper 0–300mm', 'Micrometer 0–25mm', 'Micrometer 25–50mm', 'Digital Caliper 0–200mm'];

const blankRow = () => ({
  feature: '',
  nominal: 0,
  upperTol: 0.05,
  lowerTol: 0.02,
  actual: '',
});

const VernierScreen = () => {
  const cid = cleanCustomerId(localStorage.getItem('CustomerID'));
  const [components, setComponents] = useState([]);
  const [history, setHistory] = useState([]);

  const [partCode, setPartCode] = useState('');
  const [serial, setSerial] = useState('');
  const [instrument, setInstrument] = useState(INSTRUMENTS[0]);
  const [operator, setOperator] = useState(localStorage.getItem('firstName')?.replace(/"/g, '') || 'Operator');
  const [rows, setRows] = useState([
    { feature: 'OD ø', nominal: 25.00, upperTol: 0.05, lowerTol: 0.02, actual: '' },
    { feature: 'ID ø', nominal: 12.00, upperTol: 0.03, lowerTol: 0.03, actual: '' },
    { feature: 'Length', nominal: 80.00, upperTol: 0.10, lowerTol: 0.10, actual: '' },
  ]);

  useEffect(() => {
    (async () => {
      try { const r = await customerbasedshift(cid, 'component'); setComponents(Array.isArray(r?.[0]?.value) ? r[0].value : []); } catch { setComponents([]); }
      try { const r = await customerbasedshift(cid, STORAGE_KEY); setHistory(Array.isArray(r?.[0]?.value) ? r[0].value : []); } catch { setHistory([]); }
    })();
  }, [cid]);

  const evalRow = (r) => {
    if (r.actual === '' || Number.isNaN(Number(r.actual))) return null;
    const v = Number(r.actual);
    const hi = Number(r.nominal) + Number(r.upperTol);
    const lo = Number(r.nominal) - Number(r.lowerTol);
    return v >= lo && v <= hi ? 'PASS' : 'FAIL';
  };

  const overall = (() => {
    const evals = rows.map(evalRow);
    if (evals.every(e => e === null)) return null;
    if (evals.some(e => e === 'FAIL')) return 'FAIL';
    if (evals.some(e => e === null)) return 'PARTIAL';
    return 'PASS';
  })();

  const partOptions = components.slice(0, 30).map(c => ({ code: c.component_number || '', name: c.component_name || '' }));

  const save = async () => {
    if (!partCode || rows.every(r => r.actual === '')) return;
    const entry = {
      id: 'QC-V-' + Date.now(), ts: Date.now(),
      partCode, serial, instrument, operator, result: overall,
      rows: rows.map(r => ({ ...r, actual: r.actual === '' ? null : Number(r.actual), result: evalRow(r) })),
    };
    const next = [entry, ...history].slice(0, 200);
    setHistory(next);
    try { await shiftadd({ [STORAGE_KEY]: next }, cid, 'SERVER_SCOPE'); }
    catch (e) { console.error('save failed', e); }
    setRows(rows.map(r => ({ ...r, actual: '' })));
    setSerial('');
  };

  const tone = overall === 'PASS' ? '#16a34a' : overall === 'FAIL' ? '#dc2626' : overall === 'PARTIAL' ? '#f59e0b' : '#94a3b8';
  const toneBg = overall === 'PASS' ? '#dcfce7' : overall === 'FAIL' ? '#fee2e2' : overall === 'PARTIAL' ? '#fef3c7' : '#f3f4f6';

  return (
    <Box sx={{ p: { xs: 2, md: 3 }, bgcolor: 'background.default', minHeight: '100vh' }}>
      <Stack direction="row" alignItems="center" spacing={1.5} sx={{ mb: 2 }}>
        <StraightenIcon sx={{ fontSize: 28, color: 'primary.main' }} />
        <Typography variant="h5" sx={{ fontWeight: 800, color: 'text.primary' }}>Vernier Caliper · Multi-Dimension Inspection</Typography>
        <Chip label={`${history.length} records`} size="small" sx={{ ml: 'auto' }} />
      </Stack>

      <Card sx={{ p: 2.5, mb: 2, borderRadius: 3, border: '1px solid', borderColor: 'divider' }}>
        <Stack direction={{ xs: 'column', md: 'row' }} spacing={1.5} sx={{ mb: 2 }}>
          <TextField select label="Component" value={partCode} onChange={(e) => setPartCode(e.target.value)} size="small" sx={{ flex: 2 }}>
            {partOptions.map(p => (
              <MenuItem key={p.code} value={p.code}>
                <span style={{ fontFamily: 'monospace', fontWeight: 700, marginRight: 8 }}>{p.code}</span>
                <span style={{ color: '#6b7280', fontSize: 12 }}>{p.name}</span>
              </MenuItem>
            ))}
          </TextField>
          <TextField label="Serial / Batch" value={serial} onChange={(e) => setSerial(e.target.value)} size="small" sx={{ flex: 1 }} />
          <TextField select label="Instrument" value={instrument} onChange={(e) => setInstrument(e.target.value)} size="small" sx={{ flex: 1.5 }}>
            {INSTRUMENTS.map(i => <MenuItem key={i} value={i}>{i}</MenuItem>)}
          </TextField>
          <TextField label="Operator" value={operator} onChange={(e) => setOperator(e.target.value)} size="small" sx={{ flex: 1 }} />
        </Stack>

        {/* Measurement rows */}
        <Box sx={{ display: 'grid', gridTemplateColumns: '1.5fr 1fr 0.7fr 0.7fr 1.2fr 0.8fr 32px', gap: 1, alignItems: 'center', mb: 1 }}>
          <Typography sx={{ fontSize: 11, fontWeight: 800, color: 'text.secondary', letterSpacing: 0.8 }}>FEATURE</Typography>
          <Typography sx={{ fontSize: 11, fontWeight: 800, color: 'text.secondary', letterSpacing: 0.8 }}>NOMINAL (mm)</Typography>
          <Typography sx={{ fontSize: 11, fontWeight: 800, color: 'text.secondary', letterSpacing: 0.8 }}>+ TOL</Typography>
          <Typography sx={{ fontSize: 11, fontWeight: 800, color: 'text.secondary', letterSpacing: 0.8 }}>− TOL</Typography>
          <Typography sx={{ fontSize: 11, fontWeight: 800, color: 'text.secondary', letterSpacing: 0.8 }}>ACTUAL (mm)</Typography>
          <Typography sx={{ fontSize: 11, fontWeight: 800, color: 'text.secondary', letterSpacing: 0.8 }}>STATUS</Typography>
          <Box />
        </Box>
        {rows.map((r, idx) => {
          const ev = evalRow(r);
          const rowTone = ev === 'PASS' ? '#16a34a' : ev === 'FAIL' ? '#dc2626' : '#9ca3af';
          return (
            <Box key={idx} sx={{
              display: 'grid', gridTemplateColumns: '1.5fr 1fr 0.7fr 0.7fr 1.2fr 0.8fr 32px',
              gap: 1, alignItems: 'center', py: 1, borderBottom: '1px solid', borderColor: 'divider',
            }}>
              <TextField size="small" value={r.feature} onChange={(e) => { const n = [...rows]; n[idx].feature = e.target.value; setRows(n); }} placeholder="e.g. OD ø" />
              <TextField size="small" type="number" value={r.nominal} onChange={(e) => { const n = [...rows]; n[idx].nominal = e.target.value; setRows(n); }} inputProps={{ step: 0.01 }} />
              <TextField size="small" type="number" value={r.upperTol} onChange={(e) => { const n = [...rows]; n[idx].upperTol = e.target.value; setRows(n); }} inputProps={{ step: 0.01 }} />
              <TextField size="small" type="number" value={r.lowerTol} onChange={(e) => { const n = [...rows]; n[idx].lowerTol = e.target.value; setRows(n); }} inputProps={{ step: 0.01 }} />
              <TextField size="small" type="number" value={r.actual} onChange={(e) => { const n = [...rows]; n[idx].actual = e.target.value; setRows(n); }}
                inputProps={{ step: 0.001 }}
                sx={{ '& input': { fontFamily: 'monospace', fontWeight: 700, color: rowTone } }} />
              <Chip label={ev || '—'} size="small" sx={{
                bgcolor: ev === 'PASS' ? '#dcfce7' : ev === 'FAIL' ? '#fee2e2' : 'action.hover',
                color: ev === 'PASS' ? '#14532d' : ev === 'FAIL' ? '#7a0000' : 'text.secondary',
                fontWeight: 800,
              }} />
              <IconButton size="small" onClick={() => setRows(rows.filter((_, i) => i !== idx))}>
                <DeleteOutlineIcon fontSize="small" />
              </IconButton>
            </Box>
          );
        })}
        <Stack direction="row" spacing={1.5} sx={{ mt: 2 }}>
          <Button startIcon={<AddIcon />} variant="outlined" size="small" onClick={() => setRows([...rows, blankRow()])}>
            Add feature
          </Button>
          <Box sx={{ flexGrow: 1 }} />
          <Chip label={`OVERALL: ${overall || '—'}`} sx={{
            bgcolor: toneBg, color: tone, fontWeight: 900, fontSize: 13, px: 1.5, py: 2, border: `2px solid ${tone}`,
          }} />
          <Button variant="contained" color="primary" disabled={!partCode || rows.every(r => r.actual === '')}
            onClick={save} sx={{ textTransform: 'none', fontWeight: 800, px: 3 }}>
            Save inspection
          </Button>
        </Stack>
      </Card>

      {/* Recent */}
      <Card sx={{ p: 2, borderRadius: 3, border: '1px solid', borderColor: 'divider' }}>
        <Typography sx={{ fontSize: 13, fontWeight: 800, color: 'text.secondary', mb: 1.5, letterSpacing: 1 }}>
          RECENT INSPECTIONS
        </Typography>
        {history.length === 0 && <Typography sx={{ color: 'text.disabled', fontSize: 13, py: 2 }}>No records yet.</Typography>}
        {history.slice(0, 20).map(h => (
          <Box key={h.id} sx={{ display: 'flex', alignItems: 'center', gap: 1.5, py: 1, borderBottom: '1px solid', borderColor: 'divider' }}>
            <Chip label={h.result} size="small" sx={{
              bgcolor: h.result === 'PASS' ? '#dcfce7' : h.result === 'FAIL' ? '#fee2e2' : '#fef3c7',
              color: h.result === 'PASS' ? '#14532d' : h.result === 'FAIL' ? '#7a0000' : '#92400e',
              fontWeight: 800, minWidth: 80,
            }} />
            <Typography sx={{ fontFamily: 'monospace', fontWeight: 700, fontSize: 13 }}>{h.partCode}</Typography>
            <Typography sx={{ fontSize: 12, color: 'text.secondary' }}>{h.serial} · {h.instrument} · {h.rows.length} features</Typography>
            <Typography sx={{ fontSize: 11, color: 'text.disabled', ml: 'auto' }}>
              {new Date(h.ts).toLocaleString('en-IN', { dateStyle: 'short', timeStyle: 'short' })}
            </Typography>
          </Box>
        ))}
      </Card>
    </Box>
  );
};

export default VernierScreen;
